import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Ticket, Plus, Copy, Check, X, Clock, Users, Calendar, RefreshCw, Loader2, Ban } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';

export default function TrialCodesPanel({ isDarkMode }) {
  const { api } = useAuth();
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [copiedCode, setCopiedCode] = useState('');
  const [justCreated, setJustCreated] = useState(null);
  const [form, setForm] = useState({
    months: 1,
    max_redemptions: 10,
    expires_at: '',
    label: '',
    notes: '',
  });

  const cardClass = isDarkMode
    ? 'bg-[#14161a] border border-white/10'
    : 'bg-white border border-gray-200';
  const textPrimary = isDarkMode ? 'text-white' : 'text-gray-900';
  const textSecondary = isDarkMode ? 'text-gray-400' : 'text-gray-600';
  const inputClass = isDarkMode
    ? 'bg-[#0a0b0f] border-white/10 text-white placeholder-gray-500'
    : 'bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400';

  const fetchCodes = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/admin/trial-codes');
      setCodes(res.data?.codes || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load trial codes');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchCodes();
  }, [fetchCodes]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.months || form.months < 1 || form.months > 12) {
      toast.error('Trial months must be between 1 and 12');
      return;
    }
    if (!form.max_redemptions || form.max_redemptions < 1) {
      toast.error('Max redemptions must be at least 1');
      return;
    }
    setCreating(true);
    try {
      const payload = {
        months: Number(form.months),
        max_redemptions: Number(form.max_redemptions),
      };
      if (form.expires_at) payload.expires_at = new Date(form.expires_at).toISOString();
      if (form.label) payload.label = form.label;
      if (form.notes) payload.notes = form.notes;
      const res = await api.post('/api/admin/trial-codes', payload);
      toast.success(`Code ${res.data.code} generated!`);
      setJustCreated(res.data);
      setForm({ months: 1, max_redemptions: 10, expires_at: '', label: '', notes: '' });
      fetchCodes();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to create code');
    } finally {
      setCreating(false);
    }
  };

  const handleCopy = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedCode(code);
      setTimeout(() => setCopiedCode(''), 1500);
    } catch {
      toast.error('Could not copy to clipboard');
    }
  };

  const handleRevoke = async (codeId, code) => {
    if (!window.confirm(`Revoke code ${code}? No new redemptions will be allowed.`)) return;
    try {
      await api.post(`/api/admin/trial-codes/${codeId}/revoke`);
      toast.success(`${code} revoked`);
      fetchCodes();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to revoke');
    }
  };

  const handleArchive = async (codeId, code) => {
    if (!window.confirm(`Archive code ${code} from the list?`)) return;
    try {
      await api.delete(`/api/admin/trial-codes/${codeId}`);
      toast.success(`${code} archived`);
      fetchCodes();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to archive');
    }
  };

  const statusChip = (status) => {
    const map = {
      active: { label: 'Active', cls: 'bg-emerald-400/10 text-emerald-300 border-emerald-400/20' },
      revoked: { label: 'Revoked', cls: 'bg-rose-400/10 text-rose-300 border-rose-400/20' },
      exhausted: { label: 'Used Up', cls: 'bg-slate-400/10 text-slate-300 border-slate-400/20' },
      archived: { label: 'Archived', cls: 'bg-slate-400/10 text-slate-500 border-slate-400/20' },
    };
    const m = map[status] || map.active;
    return (
      <span className={`text-[10px] px-2 py-0.5 rounded-full border ${m.cls} font-medium uppercase tracking-wider`}>
        {m.label}
      </span>
    );
  };

  return (
    <div className={`${cardClass} rounded-xl p-6`} data-testid="trial-codes-panel">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-violet-400/10 flex items-center justify-center">
          <Ticket className="text-violet-400" size={20} />
        </div>
        <div>
          <h3 className={`font-semibold ${textPrimary}`}>Trial Codes</h3>
          <p className={`text-sm ${textSecondary}`}>Generate promo codes that give N free months on any plan at signup</p>
        </div>
        <button
          onClick={fetchCodes}
          className={`ml-auto p-2 rounded-lg ${textSecondary} hover:bg-white/5`}
          title="Refresh"
          data-testid="trial-codes-refresh-btn"
        >
          <RefreshCw size={16} />
        </button>
      </div>

      {/* Just-created code banner */}
      {justCreated && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-5 rounded-xl border border-violet-400/30 bg-gradient-to-br from-violet-400/10 via-fuchsia-400/5 to-violet-400/10 p-4"
          data-testid="trial-code-created-banner"
        >
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs text-violet-300 uppercase tracking-wider font-medium">New Code Generated</div>
              <div className="mt-1 flex items-center gap-3">
                <span className={`text-2xl font-mono font-bold tracking-wider ${textPrimary}`}>{justCreated.code}</span>
                <button
                  onClick={() => handleCopy(justCreated.code)}
                  className="px-3 py-1.5 rounded-lg bg-violet-400 text-[#0a0b0f] font-medium text-sm flex items-center gap-1.5 hover:bg-violet-500 transition-colors"
                  data-testid={`trial-code-copy-${justCreated.code}`}
                >
                  {copiedCode === justCreated.code ? <Check size={14} /> : <Copy size={14} />}
                  {copiedCode === justCreated.code ? 'Copied!' : 'Copy'}
                </button>
              </div>
              <div className={`mt-2 text-xs ${textSecondary}`}>
                {justCreated.months}mo · Max {justCreated.max_redemptions} uses
                {justCreated.expires_at ? ` · Expires ${new Date(justCreated.expires_at).toLocaleDateString()}` : ''}
              </div>
            </div>
            <button onClick={() => setJustCreated(null)} className={`p-1.5 rounded-lg ${textSecondary} hover:bg-white/5`}>
              <X size={16} />
            </button>
          </div>
        </motion.div>
      )}

      {/* Create form */}
      <form onSubmit={handleCreate} className="space-y-3 mb-6" data-testid="trial-code-form">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={`block text-xs ${textSecondary} mb-1`}>Trial Months *</label>
            <input
              type="number"
              min="1"
              max="12"
              required
              value={form.months}
              onChange={(e) => setForm({ ...form, months: e.target.value })}
              className={`w-full px-3 py-2 rounded-lg ${inputClass} border focus:outline-none focus:ring-1 focus:ring-violet-400/50 text-sm`}
              data-testid="trial-code-months-input"
            />
          </div>
          <div>
            <label className={`block text-xs ${textSecondary} mb-1`}>Max Redemptions *</label>
            <input
              type="number"
              min="1"
              max="1000"
              required
              value={form.max_redemptions}
              onChange={(e) => setForm({ ...form, max_redemptions: e.target.value })}
              className={`w-full px-3 py-2 rounded-lg ${inputClass} border focus:outline-none focus:ring-1 focus:ring-violet-400/50 text-sm`}
              data-testid="trial-code-max-input"
            />
          </div>
        </div>
        <div>
          <label className={`block text-xs ${textSecondary} mb-1`}>Expires On (optional)</label>
          <input
            type="datetime-local"
            value={form.expires_at}
            onChange={(e) => setForm({ ...form, expires_at: e.target.value })}
            className={`w-full px-3 py-2 rounded-lg ${inputClass} border focus:outline-none focus:ring-1 focus:ring-violet-400/50 text-sm`}
            data-testid="trial-code-expires-input"
          />
        </div>
        <div>
          <label className={`block text-xs ${textSecondary} mb-1`}>Label / Notes (optional)</label>
          <input
            type="text"
            maxLength="40"
            placeholder="e.g. FB-ADS-Q1 or Partner: Jane Doe"
            value={form.label}
            onChange={(e) => setForm({ ...form, label: e.target.value })}
            className={`w-full px-3 py-2 rounded-lg ${inputClass} border focus:outline-none focus:ring-1 focus:ring-violet-400/50 text-sm`}
            data-testid="trial-code-label-input"
          />
        </div>
        <button
          type="submit"
          disabled={creating}
          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-violet-400 text-[#0a0b0f] font-medium hover:bg-violet-500 transition-colors disabled:opacity-50"
          data-testid="trial-code-generate-btn"
        >
          {creating ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}
          Generate Code
        </button>
      </form>

      {/* Codes list */}
      <div className="border-t border-white/5 pt-5">
        <div className={`flex items-center justify-between mb-3`}>
          <h4 className={`text-sm font-medium ${textPrimary}`}>All Codes</h4>
          <span className={`text-xs ${textSecondary}`}>{codes.length} total</span>
        </div>

        {loading ? (
          <div className={`flex items-center justify-center py-8 ${textSecondary}`}>
            <Loader2 size={18} className="animate-spin mr-2" /> Loading…
          </div>
        ) : codes.length === 0 ? (
          <div className={`text-center py-8 rounded-lg border border-dashed ${isDarkMode ? 'border-white/10' : 'border-gray-200'}`}>
            <Ticket size={24} className={`mx-auto mb-2 ${textSecondary}`} />
            <p className={`text-sm ${textSecondary}`}>No trial codes yet — generate one above.</p>
          </div>
        ) : (
          <div className="space-y-2 max-h-[320px] overflow-y-auto pr-1" data-testid="trial-codes-list">
            {codes.map((c) => {
              const remaining = (c.max_redemptions || 0) - (c.redemptions_used || 0);
              const isActive = c.status === 'active';
              return (
                <div
                  key={c.code_id}
                  className={`p-3 rounded-lg border ${isDarkMode ? 'bg-[#0a0b0f] border-white/5' : 'bg-gray-50 border-gray-200'}`}
                  data-testid={`trial-code-row-${c.code}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`font-mono font-bold text-sm ${textPrimary}`}>{c.code}</span>
                        {statusChip(c.status)}
                        {c.label && <span className={`text-xs ${textSecondary}`}>· {c.label}</span>}
                      </div>
                      <div className={`mt-1.5 flex items-center gap-3 text-xs ${textSecondary} flex-wrap`}>
                        <span className="flex items-center gap-1"><Clock size={12} />{c.months}mo free</span>
                        <span className="flex items-center gap-1"><Users size={12} />{c.redemptions_used || 0}/{c.max_redemptions} uses · {remaining} left</span>
                        {c.expires_at && (
                          <span className="flex items-center gap-1"><Calendar size={12} />Expires {new Date(c.expires_at).toLocaleDateString()}</span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button
                        onClick={() => handleCopy(c.code)}
                        className={`p-1.5 rounded-lg ${textSecondary} hover:bg-white/5`}
                        title="Copy"
                        data-testid={`trial-code-copy-btn-${c.code}`}
                      >
                        {copiedCode === c.code ? <Check size={14} className="text-emerald-400" /> : <Copy size={14} />}
                      </button>
                      {isActive && (
                        <button
                          onClick={() => handleRevoke(c.code_id, c.code)}
                          className={`p-1.5 rounded-lg ${textSecondary} hover:bg-rose-400/10 hover:text-rose-300`}
                          title="Revoke"
                          data-testid={`trial-code-revoke-btn-${c.code}`}
                        >
                          <Ban size={14} />
                        </button>
                      )}
                      <button
                        onClick={() => handleArchive(c.code_id, c.code)}
                        className={`p-1.5 rounded-lg ${textSecondary} hover:bg-white/5`}
                        title="Archive"
                        data-testid={`trial-code-archive-btn-${c.code}`}
                      >
                        <X size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
